/**
 * Sitewide slide-out drawer matching the homepage mock:
 * lockup + close, line-icon nav, pink auth pills.
 * Signed-in accounts see site pages, then Talker / Listener items,
 * then Account settings and Log out at the bottom.
 */
import React, { useEffect, useState } from 'react';
import classNames from 'classnames';

import { FormattedMessage } from '../../util/reactIntl';
import {
  getDrawerNavLinks,
  getSignedInDrawerAccountAction,
  getSignedInDrawerNav,
  getSignedInDrawerSiteLinks,
  isDrawerCurrentPage,
  PUBLIC_NAV_LINKS,
} from '../../util/siteNavLinks';
import { landingPageLinkTo } from '../../util/publicHome';
import { canSwitchNavMode, NAV_MODE_LISTENER, resolveNavMode } from '../../util/siteNavMode';

import { InlineTextButton } from '../Button/Button';
import NamedLink from '../NamedLink/NamedLink';
import BrandLockup from '../BrandLockup/BrandLockup';
import ModeSwitchToggle from '../ModeSwitchToggle/ModeSwitchToggle';
import { Avatar } from '../Avatar/Avatar';

import SiteDrawerIcon from './SiteDrawerIcons';
import css from './SiteDrawer.module.css';

export { PUBLIC_NAV_LINKS };

const isCurrentPage = (currentPage, page) => isDrawerCurrentPage(currentPage, page);

const roleParams = ({ currentUser, currentUserHasListings, currentUserOwnListing }) => ({
  currentUser,
  currentUserHasListings,
  currentUserOwnListing,
});

const DrawerLink = ({ link, currentPage, onClose, className }) => {
  const current = isCurrentPage(currentPage, link.name);
  return (
    <NamedLink
      name={link.name}
      to={link.to}
      params={link.params}
      onClick={onClose}
      data-current-page={current ? 'true' : undefined}
      ariaCurrent={current ? 'page' : undefined}
      className={classNames(className, current && css.active)}
    >
      <SiteDrawerIcon routeName={link.icon || link.name} />
      <span>
        <FormattedMessage id={link.id} />
      </span>
    </NamedLink>
  );
};

/**
 * Mock slide-out drawer used on guest home and every Topbar page.
 *
 * @param {Object} props
 * @returns {JSX.Element}
 */
const SiteDrawer = props => {
  const {
    isAuthenticated,
    currentPage,
    inboxTab,
    locationSearch,
    currentUser,
    onLogout,
    onClose,
    onSwitchNavMode,
    currentUserHasListings,
    currentUserOwnListing,
  } = props;

  const layout = {
    ...roleParams({ currentUser, currentUserHasListings, currentUserOwnListing }),
    currentPage,
    search: locationSearch,
    inboxTab,
  };
  const storedMode = resolveNavMode(layout);
  const [modeOverride, setModeOverride] = useState(null);
  useEffect(() => {
    setModeOverride(null);
  }, [currentPage, locationSearch, inboxTab]);
  const mode = isAuthenticated ? modeOverride || storedMode : null;
  const guestLinks = getDrawerNavLinks(layout);
  const signedInLinks = isAuthenticated ? getSignedInDrawerNav({ ...layout, mode }) : [];
  const siteLinks = isAuthenticated ? getSignedInDrawerSiteLinks() : [];
  const accountAction = isAuthenticated
    ? getSignedInDrawerAccountAction({ ...layout, mode })
    : null;
  // Sam Oct 25: any earner (listen, teach or both = published own listing) never sees Become an earner.
  const earnerAcct = (layout?.currentUserOwnListing?.attributes?.state || layout?.currentUserOwnListing?.state) === 'published';
  const canSwitch = isAuthenticated && canSwitchNavMode(layout);

  const switchControl =
    accountAction?.kind === 'switch' && canSwitch ? (
      <ModeSwitchToggle
        mode={mode}
        onChange={next => {
          setModeOverride(next);
          if (typeof onSwitchNavMode === 'function') {
            onSwitchNavMode(next);
          }
        }}
        className={css.modeSwitch}
      />
    ) : null;

  return (
    <div
      className={classNames(css.panel, mode === NAV_MODE_LISTENER && css.listenerMode)}
      data-drawer-mode={isAuthenticated ? mode : 'guest'}
    >
      <div className={css.header} data-drawer-brand="ear-heart">
        <NamedLink
          className={css.brandLink}
          name="LandingPage"
          to={landingPageLinkTo({ isAuthenticated })}
          onClick={onClose}
          data-drawer-home="true"
        >
          <BrandLockup size="menu" />
        </NamedLink>
        <div className={css.headerEnd}>
          {isAuthenticated ? (
            <Avatar
              className={css.drawerAvatar}
              user={currentUser}
              listing={currentUserOwnListing}
              disableProfileLink
            />
          ) : null}
          <button className={css.close} type="button" onClick={onClose} aria-label="Close menu">
            ×
          </button>
        </div>
      </div>
      <div className={css.body}>
        {switchControl}
        {isAuthenticated ? (
          <>
            <nav
              className={classNames(css.nav, css.primaryCard)}
              aria-label="Account"
              data-drawer-primary="true"
            >
              {signedInLinks
                .filter(link => link.group !== 'tips')
                // Sam Oct 24 late: buyers don't get My Profile here (bottom nav says Become an earner).
                .filter(link => !(accountAction?.kind === 'becomeListener' && !earnerAcct && link.id === 'SiteDrawer.myTalkingProfile'))
                .map(link => (
                  <DrawerLink
                    key={`${link.id}-${link.name}-${link.params?.tab || ''}-${link.to?.hash || ''}`}
                    link={link}
                    currentPage={currentPage}
                    onClose={onClose}
                    className={classNames(css.navLink, css.primaryLink)}
                  />
                ))}
            </nav>
            <nav className={css.nav} aria-label="Tips" data-drawer-tips="true">
              {signedInLinks
                .filter(link => link.group === 'tips')
                .map(link => (
                  <DrawerLink
                    key={`${link.id}-${link.name}-${link.params?.tab || ''}-${link.to?.hash || ''}`}
                    link={link}
                    currentPage={currentPage}
                    onClose={onClose}
                    className={classNames(css.navLink, css.tipsLink)}
                  />
                ))}
            </nav>
            <div className={css.divider} role="separator" />
            <nav className={css.siteNav} aria-label="Site pages" data-drawer-site="true">
              {siteLinks.map(link => (
                <DrawerLink
                  key={`${link.id}-${link.name}`}
                  link={link}
                  currentPage={currentPage}
                  onClose={onClose}
                  className={classNames(
                    css.siteLink,
                    isCurrentPage(currentPage, link.name) && css.siteActive
                  )}
                />
              ))}
            </nav>
            <div className={css.footer}>
              <div className={css.divider} role="separator" />
              {accountAction?.kind === 'becomeListener' && !earnerAcct ? (
                <NamedLink
                  name="BecomeAnEarnerPage"
                  onClick={onClose}
                  className={css.switchButton}
                >
                  <FormattedMessage id={accountAction.id} />
                </NamedLink>
              ) : (
                // Sam Oct 24: earners see Promote here instead.
                <NamedLink name="PromotePage" onClick={onClose} className={css.switchButton} data-drawer-promote="true">
                  Promote
                </NamedLink>
              )}
              <NamedLink
                name="AccountSettingsPage"
                onClick={onClose}
                data-current-page={
                  isCurrentPage(currentPage, 'AccountSettingsPage') ? 'true' : undefined
                }
                ariaCurrent={
                  isCurrentPage(currentPage, 'AccountSettingsPage') ? 'page' : undefined
                }
                className={classNames(
                  css.quietLink,
                  isCurrentPage(currentPage, 'AccountSettingsPage') && css.active
                )}
              >
                <FormattedMessage id="TopbarMobileMenu.accountSettingsLink" />
              </NamedLink>
              <InlineTextButton rootClassName={css.quietButton} onClick={onLogout}>
                <FormattedMessage id="TopbarMobileMenu.logoutLink" />
              </InlineTextButton>
            </div>
          </>
        ) : (
          <>
            <nav className={css.nav} aria-label="Site">
              {guestLinks.map(link => (
                <DrawerLink
                  key={`${link.id}-${link.name}-${link.params?.tab || ''}-${link.to?.hash || ''}`}
                  link={link}
                  currentPage={currentPage}
                  onClose={onClose}
                  className={css.navLink}
                />
              ))}
            </nav>
            <div className={css.footer}>
              <NamedLink name="LoginPage" onClick={onClose} className={css.outlinePill}>
                <FormattedMessage id="SiteDrawer.login" />
              </NamedLink>
              <NamedLink name="SignupPage" onClick={onClose} className={css.solidPill}>
                <FormattedMessage id="SiteDrawer.signup" />
              </NamedLink>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SiteDrawer;
